import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
  Rating as MuiRating,
  alpha,
  Paper,
  Avatar
} from '@mui/material';
import { Place, Star, LocalCafe, Schedule, Coffee } from '@mui/icons-material';
import { Cafe } from '../../types/explore';
import { CardGridSkeleton } from '../skeletons';

interface CafeListProps {
  cafes: Cafe[];
  selectedCafe: Cafe | null;
  onCafeSelect: (cafe: Cafe) => void;
  onNavigate: (cafe: Cafe) => void;
  loading: boolean;
  darkMode: boolean;
}

const CafeList: React.FC<CafeListProps> = ({
  cafes,
  selectedCafe,
  onCafeSelect,
  onNavigate,
  loading,
  darkMode
}) => {
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';
  const lokiGradient = 'linear-gradient(135deg, #051814 0%, #0F3B2E 100%)';
  
  const discordFont = {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  };

  const glassStyle = {
    backdropFilter: 'blur(20px)',
    border: `1px solid ${alpha(darkMode ? goldColor : deepGreen, 0.1)}`,
    boxShadow: darkMode ? '0 10px 30px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.05)',
  };

  if (loading) {
    return <CardGridSkeleton count={6} size={{ xs: 12, sm: 6, md: 4 }} label="Discovering cafes" />;
  }
  
  if (cafes.length === 0) {
    return (
      <Paper 
        elevation={0}
        sx={{ 
          p: 6, 
          borderRadius: '24px',
          background: darkMode 
            ? 'linear-gradient(135deg, rgba(5, 24, 20, 0.8) 0%, rgba(15, 59, 46, 0.8) 100%)'
            : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,240,240,0.9) 100%)',
          ...glassStyle,
          textAlign: 'center'
        }}
      >
        <Coffee sx={{ 
          fontSize: 80, 
          color: darkMode ? alpha('#fff', 0.1) : alpha(deepGreen, 0.1), 
          mb: 3 
        }} />
        <Typography variant="h5" sx={{ 
          ...discordFont,
          color: darkMode ? '#fff' : deepGreen, 
          mb: 2,
          fontSize: '1.5rem'
        }}>
          No cafes found yet
        </Typography>
        <Typography variant="body1" sx={{ 
          color: darkMode ? alpha('#fff', 0.6) : alpha(deepGreen, 0.6),
          maxWidth: 300,
          mx: 'auto',
          mb: 3
        }}>
          Search for a location or use "My Location" to discover amazing coffee spots near you.
        </Typography>
      </Paper>
    );
  }
  
  return (
    <Paper 
      elevation={0}
      sx={{ 
        borderRadius: '24px',
        background: darkMode 
          ? 'linear-gradient(135deg, rgba(5, 24, 20, 0.9) 0%, rgba(15, 59, 46, 0.9) 100%)'
          : 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240,240,240,0.95) 100%)',
        ...glassStyle,
        overflow: 'hidden',
        height: '100%'
      }}
    >
      {/* Header */}
      <Box sx={{ 
        p: 3, 
        background: lokiGradient,
        borderBottom: `1px solid ${alpha(goldColor, 0.2)}`
      }}>
        <Typography variant="h5" sx={{ 
          ...discordFont,
          color: '#fff',
          fontSize: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <LocalCafe sx={{ color: goldColor }} />
          Nearby Cafes ({cafes.length})
        </Typography>
        <Typography variant="body2" sx={{ 
          color: alpha('#fff', 0.7),
          mt: 0.5
        }}>
          Click to view details and navigate
        </Typography>
      </Box>
      
      {/* Cafe List */}
      <List sx={{ 
        maxHeight: 600, 
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: darkMode ? alpha('#fff', 0.05) : alpha(deepGreen, 0.05),
          borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: goldColor,
          borderRadius: '10px',
        }
      }}>
        {cafes.map((cafe, index) => (
          // CafeList.tsx - Update the ListItem section
<ListItem
  key={cafe.placeId}
  disablePadding
  sx={{
    borderBottom: index < cafes.length - 1 
      ? `1px solid ${alpha(darkMode ? goldColor : deepGreen, 0.05)}` 
      : 'none',
    background: selectedCafe?.placeId === cafe.placeId 
      ? darkMode 
        ? `linear-gradient(90deg, ${alpha(goldColor, 0.2)} 0%, transparent 100%)`
        : `linear-gradient(90deg, ${alpha(goldColor, 0.1)} 0%, transparent 100%)`
      : 'transparent',
    position: 'relative',
    '&::before': selectedCafe?.placeId === cafe.placeId ? {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: '4px',
      background: goldColor,
      borderRadius: '0 4px 4px 0'
    } : {}
  }}
>
  <ListItemButton
    onClick={() => onCafeSelect(cafe)}
    sx={{
      py: 2.5,
      px: 3,
      '&:hover': {
        background: darkMode 
          ? `linear-gradient(90deg, ${alpha(goldColor, 0.1)} 0%, transparent 100%)`
          : `linear-gradient(90deg, ${alpha(goldColor, 0.05)} 0%, transparent 100%)`
      }
    }}
  >
    <ListItemIcon sx={{ minWidth: 60, mr: 2 }}>
      {cafe.photos && cafe.photos.length > 0 ? (
        <Box
          component="img"
          src={cafe.photos[0]} // Show first photo
          alt={cafe.name}
          sx={{
            width: 80,
            height: 80,
            borderRadius: '8px',
            border: `2px solid ${alpha(goldColor, 0.3)}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            objectFit: 'cover',
            bgcolor: alpha(goldColor, 0.1) // Fallback background
          }}
          onError={(e) => {
            // If image fails to load, show fallback
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            // You could show fallback icon here
          }}
        />
      ) : (
        <Avatar sx={{ 
          width: 56, 
          height: 56, 
          borderRadius: '12px',
          bgcolor: alpha(goldColor, 0.2),
          color: goldColor,
          fontWeight: 'bold',
          fontSize: '1.5rem'
        }}>
          <LocalCafe />
        </Avatar>
      )}
    </ListItemIcon>
              
              <ListItemText
                primary={
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      ...discordFont,
                      color: darkMode ? '#fff' : deepGreen,
                      mb: 0.5,
                      fontSize: '1.1rem'
                    }}
                  >
                    {cafe.name}
                  </Typography>
                }
                secondary={
                  <Box sx={{ mt: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Place sx={{ fontSize: 14, color: goldColor, opacity: 0.8 }} />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: darkMode ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
                          fontSize: '0.85rem'
                        }}
                      >
                        {cafe.address.length > 35 ? `${cafe.address.substring(0, 35)}...` : cafe.address}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 1.5 }}>
                      {cafe.rating !== undefined && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <MuiRating 
                            value={cafe.rating} 
                            readOnly 
                            size="small" 
                            precision={0.5}
                            sx={{ color: goldColor }}
                          />
                          <Typography variant="caption" sx={{ 
                            color: darkMode ? alpha('#fff', 0.6) : alpha(deepGreen, 0.6),
                            fontWeight: 600
                          }}>
                            {cafe.rating.toFixed(1)}
                          </Typography>
                        </Box>
                      )}
                      
                      {cafe.priceLevel && (
                        <Chip
                          label={'$'.repeat(cafe.priceLevel)}
                          size="small"
                          sx={{ 
                            color: goldColor, 
                            borderColor: goldColor,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            height: 22,
                            background: alpha(goldColor, 0.1)
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                }
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Paper>
  );
};

export default CafeList;